import { useContext, useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { Dialog, DialogContent, DialogTitle, IconButton, Pagination } from "@mui/material"
import { MdCalendarMonth, MdClose, MdLocalShipping, MdSearch } from "react-icons/md"

import TopoMenu from "../../../componentes/gerais/TopoMenu"
import MenuLateral from "../../../componentes/gerais/Menu"
import CardEndereco from "../../../componentes/form/CardEndereco"
import { Spinner } from "../../../componentes/Spinner"
import Refresher from "../../../componentes/uteis/Refresher"
import { ContextoGeral } from "../../../contextos/ContextoGeral"
import AppAPI from "../../../servicos/AppAPI"
import { configAnim } from "../../../servicos/config/configAnim"
import {
	formatarMoeda,
	opcoesMeses,
	valorServicoRelatorio,
} from "../../../utils/periodoSaldo"
import styles from "./DetalhesSaldo.module.css"

function totaisDaResposta(retorno) {
	if (!retorno?.resultado || !retorno?.dados?.servicosTotal) {
		return { valor: 0, qtde: 0 }
	}
	const t = retorno.dados.servicosTotal
	return {
		valor: Number(t.valores) || 0,
		qtde: Number(t.concluidos) || 0,
	}
}

function nomeCliente(servico) {
	return servico?.nome || servico?.["so.nome"] || servico?.["so.empresa"] || servico?.["so.nomeFantasia"] || "Cliente"
}

function dataItem(servico) {
	const bruto = servico?.data || servico?.["s.dataFinalizacao"] || servico?.["s.dataHora"] || ""
	if (!bruto || bruto === "0000-00-00 00:00:00") return ""
	const d = new Date(String(bruto).replace(" ", "T"))
	if (Number.isNaN(d.getTime())) return String(bruto)
	return d.toLocaleString("pt-BR")
}

export default function DetalhesSaldo() {
	const { dados } = useContext(ContextoGeral)
	const meses = useMemo(() => opcoesMeses(new Date(), 6), [])

	const [menu, setMenu] = useState(false)
	const [carregandoLista, setCarregandoLista] = useState(true)
	const [erro, setErro] = useState("")
	const [servicos, setServicos] = useState([])
	const [totaisPeriodo, setTotaisPeriodo] = useState({ valor: 0, qtde: 0 })
	const [busca, setBusca] = useState("")
	const [mesSel, setMesSel] = useState(meses[0]?.value)
	const [intervaloAberto, setIntervaloAberto] = useState(false)
	const [inicio, setInicio] = useState(meses[0]?.inicio)
	const [fim, setFim] = useState(meses[0]?.fim)
	const [pagina, setPagina] = useState(1)
	const [totalPaginas, setTotalPaginas] = useState(1)
	const [servicoSelecionado, setServicoSelecionado] = useState(null)

	function paramsPeriodo() {
		const mes = meses.find((m) => m.value === mesSel) || meses[0]
		if (intervaloAberto) {
			return { periodo: "intervalo", inicio, fim, idCliente: "todos", formaPgto: "todos" }
		}
		if (mes?.mesAtual) {
			return { periodo: "mesAtual", idCliente: "todos", formaPgto: "todos" }
		}
		return {
			periodo: "intervalo",
			inicio: mes?.inicio,
			fim: mes?.fim,
			idCliente: "todos",
			formaPgto: "todos",
		}
	}

	async function carregarLista(pag = pagina) {
		if (!dados?.dominio || !dados?.idProf || !dados?.token) return
		setCarregandoLista(true)
		setErro("")
		try {
			const retorno = await AppAPI.buscarRelatorioConcluidos(dados, paramsPeriodo(), pag)
			if (retorno?.codigo === "403" || retorno?.resultado === false) {
				setServicos([])
				setTotaisPeriodo({ valor: 0, qtde: 0 })
				setErro(retorno?.mensagem || "Relatório de serviços concluídos indisponível.")
				return
			}
			const lista = Array.isArray(retorno?.dados?.servicos) ? retorno.dados.servicos : []
			setServicos(lista)
			setTotaisPeriodo(totaisDaResposta(retorno))
			const qtde = Number(retorno?.dados?.servicosTotal?.concluidos) || lista.length
			setTotalPaginas(Math.max(1, Math.ceil(qtde / 20)))
		} catch {
			setErro("Não foi possível carregar o histórico.")
			setServicos([])
		} finally {
			setCarregandoLista(false)
		}
	}

	useEffect(() => {
		carregarLista(pagina)
	}, [dados?.dominio, dados?.idProf, dados?.token, mesSel, intervaloAberto, inicio, fim, pagina])

	const listaFiltrada = useMemo(() => {
		const termo = busca.trim()
		if (!termo) return servicos
		return servicos.filter((s) => String(s?.["s.id"] ?? "").includes(termo))
	}, [servicos, busca])

	function aoTrocarMes(e) {
		const value = e.target.value
		setMesSel(value)
		const mes = meses.find((m) => m.value === value)
		if (mes) {
			setInicio(mes.inicio)
			setFim(mes.fim)
		}
		setIntervaloAberto(false)
		setPagina(1)
	}

	return (
		<motion.div
			className={styles.page}
			initial={configAnim.initial}
			animate={configAnim.animate}
			exit={configAnim.exit}
			transition={configAnim.transition}
		>
			<TopoMenu voltar="/app/home" titulo="DETALHES DO SALDO" setShow={setMenu} />
			<div className={styles.body}>
				<div className={styles.busca}>
					<MdSearch size={20} aria-hidden />
					<input
						type="search"
						placeholder="Pesquisar por código"
						value={busca}
						onChange={(e) => setBusca(e.target.value)}
						aria-label="Pesquisar por código"
					/>
				</div>

				<div className={styles.filtros}>
					<button
						type="button"
						className={`${styles.filtroBtn} ${intervaloAberto ? styles["filtroBtn--ativo"] : ""} ${intervaloAberto ? styles["filtroBtn--largo"] : ""}`}
						aria-label="Filtrar por período"
						aria-pressed={intervaloAberto}
						onClick={() => setIntervaloAberto((v) => !v)}
					>
						<MdCalendarMonth size={20} />
						{intervaloAberto && <span>Intervalo</span>}
					</button>
					{!intervaloAberto && (
						<select
							className={styles.filtroSelect}
							value={mesSel}
							onChange={aoTrocarMes}
							aria-label="Filtrar por mês"
						>
							{meses.map((m) => (
								<option key={m.value} value={m.value}>{m.label}</option>
							))}
						</select>
					)}
				</div>

				{intervaloAberto && (
					<div className={styles.datas}>
						<div>
							<label htmlFor="saldo-inicio">De</label>
							<input id="saldo-inicio" type="date" value={inicio} onChange={(e) => { setInicio(e.target.value); setPagina(1) }} />
						</div>
						<div>
							<label htmlFor="saldo-fim">Até</label>
							<input id="saldo-fim" type="date" value={fim} onChange={(e) => { setFim(e.target.value); setPagina(1) }} />
						</div>
					</div>
				)}

				<div className={styles.totais}>
					<div className={styles.totalCard}>
						<span>Recebidos</span>
						<strong>{formatarMoeda(totaisPeriodo.valor)}</strong>
					</div>
				</div>

				{erro && (
					<div className={styles.erro}>
						{erro}
						<Refresher onClick={() => { setPagina(1); carregarLista(1) }} />
					</div>
				)}

				{carregandoLista ? (
					<Spinner />
				) : listaFiltrada.length === 0 && !erro ? (
					<p className={styles.vazio}>Nenhum serviço concluído neste período.</p>
				) : (
					listaFiltrada.map((servico) => (
						<button
							type="button"
							key={servico["s.id"]}
							className={styles.item}
							onClick={() => setServicoSelecionado(servico)}
							aria-label={`Ver detalhes do serviço ${servico["s.id"]}`}
						>
							<span className={styles.itemIco} aria-hidden>
								<MdLocalShipping size={20} />
							</span>
							<div className={styles.itemCorpo}>
								<div className={styles.itemTitulo}>{servico["s.id"]}</div>
								<div className={styles.itemMeta}>{nomeCliente(servico)}</div>
								<div className={styles.itemMeta}>{dataItem(servico)}</div>
							</div>
							<span className={styles.itemValor}>{formatarMoeda(valorServicoRelatorio(servico))}</span>
						</button>
					))
				)}

				{totalPaginas > 1 && (
					<div className={styles.paginacao}>
						<Pagination
							page={pagina}
							count={totalPaginas}
							onChange={(_, val) => setPagina(val)}
							sx={{ backgroundColor: "transparent", filter: "drop-shadow(none)" }}
						/>
					</div>
				)}
			</div>

			<Dialog
				open={!!servicoSelecionado}
				onClose={() => setServicoSelecionado(null)}
				fullWidth
				maxWidth="sm"
				scroll="body"
				PaperProps={{ className: styles.modalPapel }}
			>
				<DialogTitle className={styles.modalTitulo}>
					<div className={styles.modalCabecalho}>
						<span>#{servicoSelecionado?.["s.id"]}</span>
						<IconButton onClick={() => setServicoSelecionado(null)} aria-label="Fechar">
							<MdClose size={24} />
						</IconButton>
					</div>
					{servicoSelecionado && (
						<div className={styles.modalSub}>
							<span>{nomeCliente(servicoSelecionado)}</span>
							<strong>{formatarMoeda(valorServicoRelatorio(servicoSelecionado))}</strong>
						</div>
					)}
				</DialogTitle>
				<DialogContent dividers className={styles.modalConteudo}>
					<p className={styles.modalSecao}>Endereços</p>
					{Array.isArray(servicoSelecionado?.enderecos) && servicoSelecionado.enderecos.length > 0 ? (
						servicoSelecionado.enderecos.map((endereco) => (
							<CardEndereco
								key={endereco["e.id"] ?? `${endereco["es.ponto"]}-${endereco["e.rua"]}`}
								id={endereco["es.ponto"]}
								endereco={endereco["e.rua"]}
								observacao={endereco["e.observacao"] ?? endereco["e.responsavel"]}
								icone
							/>
						))
					) : (
						<p className={styles.vazio}>Nenhum endereço disponível.</p>
					)}
					{servicoSelecionado && dataItem(servicoSelecionado) && (
						<p className={styles.modalData}>{dataItem(servicoSelecionado)}</p>
					)}
				</DialogContent>
			</Dialog>

			<MenuLateral show={menu} setShow={setMenu} />
		</motion.div>
	)
}
